import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { formatDisplayTitle } from '../formatProductTitle';
import { useProducts } from '../useProducts';
import { useSiteSettings } from '../SiteSettingsContext';
import { buildCategoryGroupCards } from '../catalogGroupUtils';
import { shopCategoryAllPath } from '../productPaths';
import { giftFinderLinkLabel, isGiftFinderLinkVisible } from '../giftFinder/giftFinderSettings';
import { customJewelryLinkLabel, isCustomJewelryLinkVisible } from '../jewelry/customJewelrySettings';

const GROUP_LINK_LIMIT = 6;

function useShopNavColumns() {
  const { categories, products } = useProducts();
  const columns = useMemo(() => {
    const sorted = [...categories].sort(
      (a, b) => a.sort_order - b.sort_order || a.slug.localeCompare(b.slug)
    );
    return sorted.map((c) => {
      const list = products[c.slug] || [];
      const groups = buildCategoryGroupCards(c, list);
      return {
        slug: c.slug,
        name: formatDisplayTitle(c.name || c.slug),
        productCount: list.length,
        groups,
      };
    });
  }, [categories, products]);
  return columns;
}

function ShopMegaMenuFooter({ onNavigate, showGiftFinder, giftFinderLabel, showCustomJewelry, customJewelryLabel }) {
  const close = () => onNavigate?.();
  return (
    <div className="shop-mega-menu__footer">
      <Link className="shop-mega-menu__footer-link" to="/shop" onClick={close}>
        Shop overview
      </Link>
      {showGiftFinder ? (
        <Link
          className="shop-mega-menu__footer-link shop-mega-menu__footer-link--accent"
          to="/gift-finder"
          onClick={close}
        >
          {giftFinderLabel}
        </Link>
      ) : null}
      {showCustomJewelry ? (
        <Link
          className="shop-mega-menu__footer-link shop-mega-menu__footer-link--accent"
          to="/custom-jewelry"
          onClick={close}
        >
          {customJewelryLabel}
        </Link>
      ) : null}
    </div>
  );
}

/** Desktop mega-menu panel inside Shop dropdown */
export function ShopMegaMenu({ onNavigate }) {
  const { settings } = useSiteSettings();
  const columns = useShopNavColumns();
  const showGiftFinder = isGiftFinderLinkVisible(settings?.store_copy, 'nav');
  const giftFinderLabel = giftFinderLinkLabel(settings?.store_copy, 'nav');
  const showCustomJewelry = isCustomJewelryLinkVisible(settings?.store_copy, 'nav');
  const customJewelryLabel = customJewelryLinkLabel(settings?.store_copy, 'nav');
  const close = () => onNavigate?.();

  if (columns.length === 0) {
    return (
      <li className="shop-mega-menu-wrap">
        <div className="shop-mega-menu" role="menu">
          <ShopMegaMenuFooter
            onNavigate={onNavigate}
            showGiftFinder={showGiftFinder}
            giftFinderLabel={giftFinderLabel}
            showCustomJewelry={showCustomJewelry}
            customJewelryLabel={customJewelryLabel}
          />
        </div>
      </li>
    );
  }

  return (
    <li className="shop-mega-menu-wrap">
      <div className="shop-mega-menu" role="menu">
        <div className="shop-mega-menu__grid">
          {columns.map((col) => (
            <div className="shop-mega-menu__column" key={col.slug}>
              <Link
                className="shop-mega-menu__category"
                to={`/shop/${col.slug}`}
                onClick={close}
                role="menuitem"
              >
                {col.name}
                {col.productCount > 0 ? (
                  <span className="shop-mega-menu__badge">{col.productCount}</span>
                ) : null}
              </Link>
              {col.groups.length > 0 ? (
                <ul className="shop-mega-menu__subs list-unstyled mb-0">
                  {col.groups.slice(0, GROUP_LINK_LIMIT).map((g) => (
                    <li key={`${col.slug}-${g.key}`}>
                      <Link
                        className="shop-mega-menu__sub-link"
                        to={`/shop/${col.slug}/${encodeURIComponent(g.key)}`}
                        onClick={close}
                        role="menuitem"
                      >
                        {g.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="shop-mega-menu__empty small text-muted mb-0">Browse all designs</p>
              )}
              {col.groups.length > GROUP_LINK_LIMIT ? (
                <Link className="shop-mega-menu__more" to={`/shop/${col.slug}`} onClick={close}>
                  +{col.groups.length - GROUP_LINK_LIMIT} more styles
                </Link>
              ) : null}
              <Link className="shop-mega-menu__view-all" to={shopCategoryAllPath(col.slug)} onClick={close}>
                View all →
              </Link>
            </div>
          ))}
        </div>
        <ShopMegaMenuFooter
          onNavigate={onNavigate}
          showGiftFinder={showGiftFinder}
          giftFinderLabel={giftFinderLabel}
          showCustomJewelry={showCustomJewelry}
          customJewelryLabel={customJewelryLabel}
        />
      </div>
    </li>
  );
}

/** Mobile nav: expandable shop tree — always shows "Shop", fills in categories when catalog is ready */
export function ShopMobileNav({ onNavigate, id = 'mobileShopNav' }) {
  const { settings } = useSiteSettings();
  const columns = useShopNavColumns();
  const showGiftFinder = isGiftFinderLinkVisible(settings?.store_copy, 'nav');
  const showCustomJewelry = isCustomJewelryLinkVisible(settings?.store_copy, 'nav');
  const close = () => onNavigate?.();

  return (
    <li className="nav-item shop-mobile-nav d-lg-none">
      <button
        type="button"
        className="nav-link shop-mobile-nav__toggle w-100 text-start border-0 bg-transparent"
        data-bs-toggle="collapse"
        data-bs-target={`#${id}`}
        aria-expanded="false"
        aria-controls={id}
      >
        Shop
        <span className="shop-mobile-nav__chev" aria-hidden />
      </button>
      <div className="collapse shop-mobile-nav__panel" id={id}>
        <ul className="list-unstyled shop-mobile-nav__list mb-0">
          <li>
            <Link className="shop-mobile-nav__link shop-mobile-nav__link--overview" to="/shop" onClick={close}>
              All collections
            </Link>
          </li>
          {columns.map((col) => (
            <li key={col.slug} className="shop-mobile-nav__group">
              <Link className="shop-mobile-nav__link shop-mobile-nav__link--cat" to={`/shop/${col.slug}`} onClick={close}>
                {col.name}
              </Link>
              {col.groups.length > 0 ? (
                <ul className="list-unstyled shop-mobile-nav__subs mb-0">
                  {col.groups.slice(0, 5).map((g) => (
                    <li key={`${col.slug}-${g.key}`}>
                      <Link
                        className="shop-mobile-nav__link shop-mobile-nav__link--sub"
                        to={`/shop/${col.slug}/${encodeURIComponent(g.key)}`}
                        onClick={close}
                      >
                        {g.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
          {showGiftFinder ? (
            <li>
              <Link className="shop-mobile-nav__link shop-mobile-nav__link--accent" to="/gift-finder" onClick={close}>
                {giftFinderLinkLabel(settings?.store_copy, 'nav')}
              </Link>
            </li>
          ) : null}
          {showCustomJewelry ? (
            <li>
              <Link className="shop-mobile-nav__link shop-mobile-nav__link--accent" to="/custom-jewelry" onClick={close}>
                {customJewelryLinkLabel(settings?.store_copy, 'nav')}
              </Link>
            </li>
          ) : null}
        </ul>
      </div>
    </li>
  );
}
